(() => {
  'use strict';
  if (window.momoClarityLoaded) return;
  window.momoClarityLoaded = true;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const players = new Map();
  // 16 Oct · silent loops live side by side; only a film with sound on claims the page
  const anotherPlaying = video => Array.from(document.querySelectorAll('video')).some(other => other !== video && !other.paused && !other.ended && !other.muted);
  const onScreen = frame => {
    if (!frame.isConnected || frame.closest('[hidden]')) return false;
    const rect = frame.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  };
  const init = root => root.querySelectorAll('[data-mc-player]').forEach(frame => {
    if (players.has(frame)) return;
    const video = frame.querySelector('video');
    const play = frame.querySelector('.mc-play');
    const status = frame.querySelector('[role="status"]');
    if (!video || !status) return;
    const auto = frame.hasAttribute('data-mc-autoplay');
    const hero = frame.classList.contains('mc-player--hero');
    // Set the initial sound once so an explicit first tap survives lazy loading.
    video.muted = auto;
    const events = new AbortController();
    const options = {signal: events.signal};
    if (matchMedia('(max-width: 700px)').matches && video.dataset.srcMobile) {
      video.dataset.src = video.dataset.srcMobile;
      if (video.dataset.posterMobile) video.poster = video.dataset.posterMobile;
    }
    let loaded = Boolean(video.getAttribute('src')), visible = false, manualPause = false, version = 0;
    let wanted = false, deliberatePlay = false, expectedPauseEvents = 0, autoFailed = false, observer;
    const sync = () => {
      if (play) play.hidden = !video.paused && !video.ended;
    };
    const state = {
      video,
      stop: () => {
        version++;
        wanted = false;
        // Observer, arbitration and tab pauses must not become a visitor's persistent pause.
        if (!video.paused) expectedPauseEvents++;
        video.pause();
        if (auto) { video.muted = true; frame.classList.remove('is-sound'); }
        sync();
      },
      resume: () => { if (auto) start(false); },
      destroy: () => {
        state.stop();
        events.abort();
        observer?.disconnect();
        players.delete(frame);
      }
    };
    players.set(frame, state);
    const start = async deliberate => {
      if (document.hidden || !onScreen(frame) || !video.dataset.src || (wanted && !video.paused)) return;
      if (!deliberate && (!auto || !visible || manualPause || autoFailed || motion.matches || navigator.connection?.saveData || document.querySelector('dialog[open]'))) return;
      // Background previews yield to every player, including other component types.
      if (!deliberate && anotherPlaying(video)) return;
      if (deliberate) {
        frame.scrollIntoView?.({block: 'nearest', inline: 'center', behavior: 'instant'});
        manualPause = false;
        autoFailed = false;
        players.forEach(other => { if (other !== state) other.stop(); });
        document.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
      }
      const attempt = ++version;
      wanted = true;
      deliberatePlay = deliberate;
      video.controls = !hero && !auto;
      if (!loaded || video.error) {
        loaded = true;
        video.src = video.dataset.src;
        video.load();
      }
      if (video.ended) video.currentTime = 0;
      status.textContent = '';
      try {
        await video.play();
        if (attempt !== version) {
          // A previous play promise must not pause a newer successful attempt.
          if (!wanted || !players.has(frame)) video.pause();
          return;
        }
        if (document.hidden || !onScreen(frame) || (!deliberate && !visible)) state.stop();
      } catch (error) {
        if (attempt === version && players.has(frame)) {
          autoFailed = true;
          state.stop();
          if (deliberate) status.textContent = hero ? 'the video could not start. press space to try again.' : 'the video could not start. tap play to try again.';
        }
      }
      sync();
    };
    play?.addEventListener('click', () => start(true), options);
    const toggleSound = () => {
      // a tap on a silent film turns its sound on [and the others off]; a second tap mutes it again
      const on = video.muted;
      video.muted = !on;
      frame.classList.toggle('is-sound', on);
      if (on) document.querySelectorAll('video').forEach(other => { if (other !== video) { other.muted = true; other.closest('[data-mc-player]')?.classList.remove('is-sound'); } });
      if (on && video.paused) start(true);
    };
    if (auto) {
      // The video surface stays keyboard-operable without a floating control badge.
      video.tabIndex = 0;
      video.setAttribute('aria-keyshortcuts', hero ? 'Space Enter' : 'Space Enter M');
      video.setAttribute('aria-description', hero
        ? 'press space or enter to pause or play.'
        : 'press space to pause or play. press enter or m to turn sound on or off.');
      video.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;
        if (event.key === ' ' || (hero && event.key === 'Enter')) {
          event.preventDefault();
          if (video.paused) start(true);
          else { manualPause = true; state.stop(); }
        } else if (!hero && (event.key === 'Enter' || event.key.toLowerCase() === 'm')) {
          event.preventDefault();
          toggleSound();
        }
      }, options);
      if (!hero) video.addEventListener('click', toggleSound, options);
    }
    if (frame.closest('.mc-review-videos')) {
      const matchFrame = () => {
        if (video.videoWidth && video.videoHeight) frame.style.setProperty('--mc-review-ratio', `${video.videoWidth}/${video.videoHeight}`);
      };
      video.addEventListener('loadedmetadata', matchFrame, options);
      matchFrame();
    }
    video.addEventListener('play', () => {
      if (video.paused) return;
      if (document.hidden || !onScreen(frame)) { state.stop(); return; }
      if (!wanted) {
        // Resuming through the browser's native controls is deliberate too.
        wanted = true;
        deliberatePlay = true;
        manualPause = false;
      }
      if (deliberatePlay) document.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
      sync();
    }, options);
    video.addEventListener('pause', () => {
      const expected = expectedPauseEvents > 0;
      if (expected) expectedPauseEvents--;
      // A queued pause event from an old attempt must not stop a newer play.
      if (!video.paused) return;
      wanted = false;
      version++;
      if (!expected && !video.ended && !document.hidden && onScreen(frame) && !anotherPlaying(video) && !document.querySelector('dialog[open]')) manualPause = true;
      sync();
    }, options);
    video.addEventListener('ended', () => {
      wanted = false;
      if (play) {
        play.querySelector('[data-mc-label]').textContent = 'watch again';
        play.setAttribute('aria-label', 'watch again: ' + video.getAttribute('aria-label'));
      }
      sync();
    }, options);
    video.addEventListener('error', () => {
      autoFailed = true;
      state.stop();
      status.textContent = hero ? 'the video could not load. press space to try again.' : 'the video could not load. tap play to try again.';
      sync();
    }, options);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => entries.forEach(entry => {
        visible = entry.isIntersecting && entry.intersectionRatio >= .2;
        if (!visible) state.stop();
        else if (auto) start(false);
      }), {threshold: [0, .2]});
      observer.observe(frame);
    }
    motion.addEventListener('change', () => {
      if (motion.matches && auto && !deliberatePlay) state.stop();
    }, options);
    frame.setAttribute('data-mc-ready', '');
  });
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    players.forEach((state, frame) => {
      if (event.target.contains(frame)) { state.destroy(); frame.removeAttribute('data-mc-ready'); }
    });
  });
  document.addEventListener('play', event => {
    if (event.target.tagName !== 'VIDEO' || event.target.paused || event.target.muted) return;
    players.forEach(state => { if (state.video !== event.target) state.stop(); });
  }, true);
  let resumeFrame = 0;
  document.addEventListener('pause', event => {
    if (event.target.tagName !== 'VIDEO' || resumeFrame) return;
    // Let each player's pause handler and visibility observer settle before arbitration.
    // This also covers a large scroll where the next preview appears before the old one stops.
    resumeFrame = requestAnimationFrame(() => {
      resumeFrame = 0;
      players.forEach(state => state.resume());
    });
  }, true);
  document.addEventListener('visibilitychange', () => {
    players.forEach(state => { if (document.hidden) state.stop(); else state.resume(); });
  });
})();
